import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import { doToast, NAVIGATE_MODAL_BACK } from 'store/actions';
import { View } from 'react-native';
import {
  APP_VERSION,
  getDeviceLineBreak,
  getInfoDump,
} from 'lib/getDeviceInformation';
import { useSelector } from 'hooks/useSelector';
import { selectUserReference } from 'store/auth/selectors';
import { Logo } from 'components/Logo';
import { CardGeneric } from 'components/CardGeneric';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import * as Clipboard from 'expo-clipboard';
import { useEsc } from 'hooks/useEsc';
import { openUrl } from 'lib/riskbase';
import {
  SUPPORT_EMAIL,
  SUPPORT_WEBSITE,
  SUPPORT_WHATSAPP,
  SUPPORT_WHATSAPP_URL,
} from 'types/types';

const LINE_BREAK = getDeviceLineBreak();

export const SupportModal: React.FC<
  ModalScreenProps<ModalScreens.Support>
> = () => {
  const dispatch = useDispatch();
  const userReference = useSelector(selectUserReference);

  const handlePressEmail = async (copy: boolean) => {
    if (copy) {
      const copied = await Clipboard.setStringAsync(SUPPORT_EMAIL);
      if (copied) {
        dispatch(doToast('Email Copied'));
      }
      return;
    }
    openUrl(
      `mailto:${SUPPORT_EMAIL}?` +
        `subject=Support Request (User: RB-${userReference}, Version: ${APP_VERSION})&` +
        'body=' +
        `Please tell us about your issue.${LINE_BREAK}${LINE_BREAK}---${LINE_BREAK} ` +
        `${getInfoDump(userReference ?? 'Unknown')}`,
    );
  };

  const handlePressWhatsapp = async (copy: boolean) => {
    if (copy) {
      const copied = await Clipboard.setStringAsync(SUPPORT_WHATSAPP);
      if (copied) {
        dispatch(doToast('WhatsApp Copied'));
        return;
      }
    }
    openUrl(SUPPORT_WHATSAPP_URL);
  };

  const handlePressWebite = async (copy: boolean) => {
    if (copy) {
      const copied = await Clipboard.setStringAsync(SUPPORT_WEBSITE);
      if (copied) {
        dispatch(doToast('Website Copied'));
      }
      return;
    }
    openUrl(SUPPORT_WEBSITE);
  };

  useEsc(() => dispatch(NAVIGATE_MODAL_BACK()));

  return (
    <PageScrollView
      header={
        <NavigationHeader
          onPressCloseButton={() => dispatch(NAVIGATE_MODAL_BACK())}
          heading="Get Support"
        />
      }
    >
      <CardGeneric margin={false}>
        <Txt size="large" style={{ textAlign: 'center' }}>
          When getting in touch, please describe your issue in detail
        </Txt>
        <Txt style={{ textAlign: 'center' }}>
          Include any errors you see and take a screenshot
        </Txt>
      </CardGeneric>
      <SeparatorHorizontal
        marginVertical={theme.paddingVertical}
        marginHorizontal={theme.paddingHorizontal}
      />
      <Card>
        <CardRow
          icon={ICONS.AtLight}
          iconSize={theme.grid * 3}
          hideMenuButton
          showMenuOnPress
          menuOptions={[
            {
              text: 'Start New Email',
              onPress: () => handlePressEmail(false),
              icon: ICONS.AtLight,
            },
            {
              text: 'Copy Email Address',
              onPress: () => handlePressEmail(true),
              icon: ICONS.CopyLight,
            },
          ]}
        >
          <View style={{ alignItems: 'flex-start' }}>
            <Txt size="large">Email us at</Txt>
            <Txt
              size="normal"
              weight="bold"
              style={{ color: theme.colors.blue }}
            >
              {SUPPORT_EMAIL}
            </Txt>
          </View>
        </CardRow>
      </Card>
      <Card>
        <CardRow
          icon={ICONS.WhatsappLight}
          iconSize={theme.grid * 3}
          hideMenuButton
          showMenuOnPress
          menuOptions={[
            {
              text: 'Open WhatsApp',
              onPress: () => handlePressWhatsapp(false),
              icon: ICONS.WhatsappLight,
            },
            {
              text: 'Copy WhatsApp Number',
              onPress: () => handlePressWhatsapp(true),
              icon: ICONS.CopyLight,
            },
          ]}
        >
          <View style={{ alignItems: 'flex-start' }}>
            <Txt size="large">WhatsApp us on</Txt>
            <Txt
              size="normal"
              weight="bold"
              style={{ color: theme.colors.blue }}
            >
              {SUPPORT_WHATSAPP}
            </Txt>
          </View>
        </CardRow>
      </Card>
      <Card margin={false}>
        <CardRow
          icon={ICONS.LifeRingLight}
          iconSize={theme.grid * 3}
          hideMenuButton
          showMenuOnPress
          menuOptions={[
            {
              text: 'Visit Website',
              onPress: () => handlePressWebite(false),
              icon: ICONS.LifeRingLight,
            },
            {
              text: 'Copy Website Address',
              onPress: () => handlePressWebite(true),
              icon: ICONS.CopyLight,
            },
          ]}
        >
          <View>
            <Txt size="large">Visit our website</Txt>
            <Txt
              size="normal"
              weight="bold"
              style={{ color: theme.colors.blue }}
            >
              {SUPPORT_WEBSITE.replace('https://', '')}
            </Txt>
          </View>
        </CardRow>
      </Card>

      <SeparatorHorizontal
        marginVertical={theme.paddingVertical}
        marginHorizontal={theme.paddingHorizontal}
      />
      <View
        style={{
          alignItems: 'center',
          flexDirection: 'row',
          justifyContent: 'center',
          flex: 1,
        }}
      >
        <Logo width={80} />
        <View
          style={{
            marginLeft: 5,
            backgroundColor: 'rgba(0,0,0,.1)',
            paddingHorizontal: 4,
            paddingVertical: 2,
            borderRadius: 4,
          }}
        >
          <Txt size="xsmall" weight="bold">
            Version {APP_VERSION}
          </Txt>
        </View>
      </View>
    </PageScrollView>
  );
};
